/**
 * Dedupe top-level @graph nodes by @id (fallback: @type + @id).
 * Nested objects (e.g. Offer inside MobileApplication) are left untouched.
 * Keeps first occurrence so a future regression cannot reintroduce duplicate entities.
 */
export function dedupeJsonLdGraph(
  graph: Array<Record<string, unknown>>,
): Array<Record<string, unknown>> {
  const seen = new Set<string>();
  return graph.filter((node) => {
    if (!node || typeof node !== 'object') {
      return false;
    }
    const id = typeof node['@id'] === 'string' ? node['@id'] : '';
    const type = Array.isArray(node['@type'])
      ? node['@type'].map(String).join(',')
      : String(node['@type'] ?? '');
    const key = id || `${type}::${JSON.stringify(node)}`;
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
}

/** Apply graph dedupe before JSON-LD serialization. */
export function prepareJsonLdForSerialize(data: unknown): unknown {
  if (!data || typeof data !== 'object') {
    return data;
  }
  const record = data as Record<string, unknown>;
  if (!Array.isArray(record['@graph'])) {
    return data;
  }
  return {
    ...record,
    '@graph': dedupeJsonLdGraph(
      record['@graph'].filter(
        (node): node is Record<string, unknown> =>
          Boolean(node) && typeof node === 'object' && !Array.isArray(node),
      ),
    ),
  };
}
